import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Leaf, ListBullets } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import FarmDiariesOptIn from "@/components/FarmDiariesOptIn";
import { IS_FARMERSPULSE } from "@/lib/site";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2000&q=80";

// -------------------------------------------------------------------------
// Sections — kept in one array so ToC + rendered anchors stay in sync
// -------------------------------------------------------------------------
const SECTIONS = [
  { id: "intro",                title: "Introduction" },
  { id: "living-network",       title: "From Almanac to Living Network" },
  { id: "farmers-view",         title: "A Farmer's View of the World" },
  { id: "local-knowledge",      title: "Local Knowledge Matters" },
  { id: "weather",              title: "Weather and Agriculture" },
  { id: "crop-intelligence",    title: "Crop Intelligence" },
  { id: "crop-reports",         title: "Reporting Crop Conditions" },
  { id: "calendars",            title: "Planting & Harvest Calendars" },
  { id: "pests",                title: "Pests and Diseases" },
  { id: "soil-pulse",           title: "Soil Pulse" },
  { id: "water-pulse",          title: "Water Pulse" },
  { id: "pollinators",          title: "Pollinators & Beneficials" },
  { id: "birds",                title: "Birds as Indicators" },
  { id: "livestock",            title: "Livestock Pulse" },
  { id: "extreme-weather",      title: "Extreme Weather" },
  { id: "climate",              title: "Climate Through Farmers' Eyes" },
  { id: "generations",          title: "Knowledge Across Generations" },
  { id: "diaries",              title: "Farm Diaries" },
  { id: "photo-docs",           title: "Photo Documentation" },
  { id: "news-research",        title: "News & Research" },
  { id: "global-local",         title: "A Global Platform" },
  { id: "schools",              title: "Schools & Young Farmers" },
  { id: "helping",              title: "Farmers Helping Farmers" },
  { id: "ai-assistant",         title: "AI as an Assistant" },
  { id: "living-almanac",       title: "A Living Almanac" },
  { id: "join",                 title: "Join the Farmers Pulse" },
];

// Plain-text version of the article body — passed to the Listen / Translate
// bar so both TTS and language toggling cover the entire piece, not only
// the hero blurb. Kept as a top-level constant so it isn't re-built each render.
const FULL_BODY_TEXT = [
  "For thousands of years, farmers have looked to the sky, the soil, plants, birds, insects, seasons, and generations of accumulated knowledge to decide when to plant, when to irrigate, when to harvest, and when to prepare for changing conditions.",
  "Traditional farmers' almanacs helped organize much of this knowledge around seasons, weather, planting cycles, the sun, the moon, and the calendar. At NaturePulse.net, we want to take that idea much further.",
  "We are introducing Farmers Pulse — a growing global agricultural information, observation, and knowledge platform designed to help farmers understand not only what normally happens in their region, but also what is happening around them right now.",
  "Farmers Pulse combines the wisdom of traditional farming with modern observations, local reporting, technology, weather information, crop knowledge, community participation, and eventually AI-powered analysis.",
  "Our goal is simple: help farmers observe better, prepare earlier, share knowledge, reduce risk, and make better decisions.",
  "From the traditional almanac to a living agricultural network. Instead of being a printed almanac updated once a year, Farmers Pulse can become a continuously updated agricultural knowledge system.",
  "A farmer's view of the world. Farmers often notice environmental changes before anyone else — flowering beginning earlier than usual, unusual insects appearing, birds disappearing from a field, soil drying faster, wells producing less water, leaves showing unusual discoloration, unexpected rainfall, stronger winds, new weeds appearing, fruit dropping prematurely, crops maturing earlier or later, and livestock behaving differently.",
  "Local knowledge matters. A weather forecast may cover an entire district, but farming happens in individual fields. Rainfall can vary dramatically within a few kilometers.",
  "Weather and agriculture. Farmers Pulse can help organize weather information around agricultural needs — rainfall, temperature, humidity, wind, thunderstorms, hail, frost, snow, heat waves, drought, flooding, soil moisture, cloud cover and extreme-weather alerts — and connect those conditions directly to crops.",
  "Crop intelligence. Farmers Pulse can build a global crop knowledge library covering cereals, pulses and legumes, roots and tubers, vegetables, fruits, and commercial crops. Each crop page can include varieties, planting periods, temperature and rainfall requirements, soil preferences, irrigation, fertilizer information, common pests and diseases, harvesting, storage, market information, farmer observations, current news and research links.",
  "Farmers reporting crop conditions. One particularly useful feature could be a simple Crop Pulse report where a farmer selects a crop and reports its condition — growth stage, condition, rainfall, pests, disease, notes and photos.",
  "Planting and harvest calendars. Farmers Pulse can create regional crop calendars showing land preparation, planting, growing, flowering, fruit development, harvest and post-harvest.",
  "Pests and diseases. Crop pests and diseases can spread rapidly. Early observations can be extremely valuable, and reports from multiple farms could help identify emerging local problems.",
  "Soil Pulse. Farmers Pulse can encourage farmers to understand and document their soils — type, color, moisture, drainage, erosion, compaction, organic matter, pH, nutrient testing and earthworm activity.",
  "Water Pulse. Water is increasingly one of agriculture's most important resources. Farmers Pulse can track wells, ponds, reservoirs, streams, irrigation canals, rainfall harvesting, groundwater conditions, flooding, drought and water restrictions.",
  "Pollinators and beneficial insects. Not every insect is a pest. Farmers depend heavily on pollinators and other beneficial organisms.",
  "Birds as agricultural indicators. Birds can also tell us a great deal about the health of farmland.",
  "Livestock Pulse. Farmers Pulse can eventually include information for livestock and poultry farmers covering feeding, breeding, pasture, heat stress, weather effects, animal health, disease alerts, water availability and productivity.",
  "Extreme weather and disaster reporting. Agriculture can be devastated by sudden events. Farmers Pulse can allow rapid reporting of drought, flooding, landslides, hail, frost, cyclones, hurricanes, tornadoes, wildfires, heat waves and severe storms.",
  "Climate change through farmers' eyes. Climate change is often discussed using global statistics. Farmers experience it locally, and their observations create a long-term archive of how planting seasons, rainfall, pests, flowering, harvest and water availability have changed.",
  "Farming knowledge across generations. Traditional farmers possess valuable knowledge accumulated through generations, and Farmers Pulse should provide a place to document that knowledge before it disappears.",
  "Farm diaries. Every farmer should eventually be able to maintain a private or public Farm Diary — recording field preparation, seeds planted, first emergence, rainfall, fertilizer, pests and harvest.",
  "Photo documentation. Smartphones have made agricultural documentation much easier. Farmers can photograph crops, pests, diseases, soil, irrigation, machinery, livestock, weeds, weather damage and harvests.",
  "Agriculture news and research. Farmers Pulse can gather and organize links from agricultural universities, government agriculture departments, extension services, research institutions, international agricultural organizations, crop research centers, farmer organizations and weather services.",
  "A global platform with local value. Agriculture is global but farming is local, so Farmers Pulse is organized geographically from world, country, state, district, city and village down to the individual farm.",
  "Schools and young farmers. Farmers Pulse should also encourage students to participate through school gardens, weather stations, rainfall monitoring, plant growth experiments, composting projects, pollinator gardens, soil studies, tree planting and crop diaries.",
  "Farmers helping farmers. The farming community itself is perhaps the most important element of Farmers Pulse — sharing problems, observations, solutions, experiments, successes and even failures.",
  "AI as a farming assistant. Artificial intelligence can eventually help organize this enormous amount of agricultural information to help farmers find and understand information faster, without replacing farmers, agronomists, extension officers, veterinarians or scientists.",
  "Building a living agricultural almanac. Farmers Pulse can look at the past, the present and eventually emerging patterns at the same time — millions of observations documenting rainfall, crops, pests, birds, pollinators, water, soil, harvests, weather and food production.",
  "Join the Farmers Pulse. Whether you are a farmer, home gardener, agricultural student, teacher, researcher, agronomist, extension worker, farm organization, agricultural business, or environmental observer — your observations can contribute to this growing knowledge network. Observe. Learn. Share. Grow.",
].join("\n\n");

// -------------------------------------------------------------------------
// FarmersPulseAlmanac
// -------------------------------------------------------------------------
export default function FarmersPulseAlmanac() {
  const p = (sub) => (IS_FARMERSPULSE ? sub : `/farmers${sub}`);
  const home = IS_FARMERSPULSE ? "/" : "/farmers";
  const shareUrl = useMemo(() => (typeof window !== "undefined" ? window.location.href : ""), []);

  // ------ SEO / social-share meta tags ------
  useEffect(() => {
    const prev = document.title;
    document.title = "Farmers Pulse: A Modern Global Almanac for Farmers · NaturePulse";
    const metas = [
      { property: "og:type",        content: "article" },
      { property: "og:title",       content: "Farmers Pulse: A Modern Global Almanac for Farmers" },
      { property: "og:description", content: "Combining traditional farming wisdom with modern observations, technology, weather, crop knowledge, and community participation — a growing global agricultural knowledge platform." },
      { property: "og:image",       content: HERO_IMAGE },
      { property: "og:image:alt",   content: "Rolling farm fields at golden hour" },
      { property: "og:url",         content: shareUrl },
      { name: "twitter:card",       content: "summary_large_image" },
      { name: "twitter:title",      content: "Farmers Pulse: A Modern Global Almanac for Farmers" },
      { name: "twitter:description", content: "A growing global agricultural information, observation, and knowledge platform for farmers everywhere." },
      { name: "twitter:image",      content: HERO_IMAGE },
      { name: "description",        content: "Farmers Pulse combines traditional farming wisdom with modern observations, weather, crop knowledge, community participation, and AI-powered analysis." },
    ];
    const created = [];
    for (const m of metas) {
      const selector = m.property
        ? `meta[property="${m.property}"]`
        : `meta[name="${m.name}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        if (m.property) el.setAttribute("property", m.property);
        if (m.name)     el.setAttribute("name", m.name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", m.content);
    }
    return () => {
      document.title = prev;
      // Only remove tags we created ourselves — leave app defaults intact.
      created.forEach((el) => el.remove());
    };
  }, [shareUrl]);

  const onJump = (e, id) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main
      className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] text-[#241209] pb-24"
      data-testid="farmers-almanac"
    >
      {/* Cinematic hero image */}
      <div
        className="relative w-full h-[46vh] md:h-[64vh] overflow-hidden"
        data-testid="farmers-almanac-hero"
      >
        <img
          src={HERO_IMAGE}
          alt="Rolling farm fields at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-[#F5EBDC]" />
        <div className="relative z-10 h-full max-w-5xl mx-auto px-5 md:px-8 flex flex-col justify-end pb-10 md:pb-14">
          <Link
            to={home}
            data-testid="farmers-almanac-back"
            className="inline-flex items-center gap-1.5 self-start rounded-full bg-white/85 backdrop-blur px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#241209] hover:bg-white"
          >
            <ArrowLeft size={11} weight="bold" /> Farmers Pulse
          </Link>
          <div className="mt-5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-white/90 flex items-center gap-1.5 drop-shadow">
            <BookOpen size={12} weight="fill" /> Farmers Pulse · Editorial
          </div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-white drop-shadow-lg max-w-4xl">
            Farmers Pulse: A Modern Global Almanac for Farmers
          </h1>
        </div>
      </div>

      {/* Body: sticky TOC + article grid */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 mt-8 md:mt-12 grid md:grid-cols-[220px_1fr] gap-8 md:gap-12">
        {/* Sticky TOC — hidden on small screens */}
        <aside className="hidden md:block" data-testid="farmers-almanac-toc">
          <div className="sticky top-28 pr-2">
            <div className="flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
              <ListBullets size={12} weight="fill" /> Contents
            </div>
            <ol className="space-y-1.5 text-sm">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => onJump(e, s.id)}
                    data-testid={`toc-link-${s.id}`}
                    className="block leading-snug text-[#3A3830] hover:text-[color:var(--np-brand,#B85F2A)] hover:underline underline-offset-4"
                  >
                    <span className="font-mono-tech text-[10px] tracking-[0.22em] text-[#8E8B7F] mr-1.5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        {/* Article */}
        <article>
          <p className="text-lg md:text-xl text-[#3A3830] leading-relaxed">
            Combining the wisdom of traditional farming with modern observations, technology, weather, crop knowledge, community participation, and eventually AI-powered analysis.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <ListenTranslateBar text={FULL_BODY_TEXT} testidPrefix="farmers-almanac-tools" />
            <ShareButton
              title="Farmers Pulse — A Modern Global Almanac for Farmers"
              text="A growing global agricultural information, observation, and knowledge platform."
              hashtags={["FarmersPulse", "NaturePulse"]}
              variant="chip"
              testid="farmers-almanac-share"
            />
          </div>

          <div className="mt-10 space-y-5 text-[#241209] text-lg leading-relaxed [&_h2]:font-serif-display [&_h2]:text-3xl [&_h2]:md:text-4xl [&_h2]:tracking-tight [&_h2]:text-[#151312] [&_h2]:mt-12 [&_h2]:mb-3 [&_h2]:scroll-mt-24 [&_h3]:font-serif-display [&_h3]:text-2xl [&_h3]:text-[#151312] [&_h3]:mt-8 [&_h3]:mb-2 [&_strong]:text-[#151312] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-[color:var(--np-brand,#B85F2A)] [&_a]:underline">

            <div id="intro" className="scroll-mt-24" />
            <p>
              For thousands of years, farmers have looked to the sky, the soil, plants, birds, insects, seasons, and generations of accumulated knowledge to decide when to plant, when to irrigate, when to harvest, and when to prepare for changing conditions.
            </p>
            <p>
              Traditional farmers' almanacs helped organize much of this knowledge around seasons, weather, planting cycles, the sun, the moon, and the calendar. At <strong>NaturePulse.net</strong>, we want to take that idea much further.
            </p>
            <p>
              We are introducing <strong>Farmers Pulse</strong> — a growing global agricultural information, observation, and knowledge platform designed to help farmers understand not only what normally happens in their region, but also what is happening around them right now.
            </p>
            <p>
              Farmers Pulse combines the wisdom of traditional farming with modern observations, local reporting, technology, weather information, crop knowledge, community participation, and eventually AI-powered analysis.
            </p>
            <p>Our goal is simple:</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              Help farmers observe better, prepare earlier, share knowledge, reduce risk, and make better decisions.
            </blockquote>

            <h2 id="living-network">From the Traditional Almanac to a Living Agricultural Network</h2>
            <p>
              The traditional farmer's almanac was valuable because it brought many kinds of useful information together in one place. Farmers could look at seasonal patterns, planting periods, rainfall expectations, temperature changes, and sometimes astronomical information to help plan agricultural activities.
            </p>
            <p>But agriculture today faces a different environment.</p>
            <p>
              Weather patterns can change rapidly. A normally dry week may bring intense rainfall. A traditional rainy season may arrive late. Temperatures may climb earlier than expected. A pest may suddenly appear in a nearby district. A plant disease may begin spreading through neighboring farms.
            </p>
            <p>This means farmers increasingly need both:</p>
            <ul>
              <li>Long-term agricultural knowledge</li>
              <li>and real-time local observations.</li>
            </ul>
            <p>Farmers Pulse is designed to connect the two.</p>
            <p>
              Instead of being a printed almanac updated once a year, Farmers Pulse can become a continuously updated agricultural knowledge system.
            </p>

            <h2 id="farmers-view">A Farmer's View of the World</h2>
            <p>Farmers often notice environmental changes before anyone else. A farmer may notice:</p>
            <ul>
              <li>flowering beginning earlier than usual</li>
              <li>unusual insects appearing</li>
              <li>birds disappearing from a field</li>
              <li>soil drying faster</li>
              <li>wells producing less water</li>
              <li>leaves showing unusual discoloration</li>
              <li>unexpected rainfall</li>
              <li>stronger winds</li>
              <li>new weeds appearing</li>
              <li>fruit dropping prematurely</li>
              <li>crops maturing earlier or later</li>
              <li>livestock behaving differently</li>
            </ul>
            <p>Individually, these observations may seem small.</p>
            <p>
              But when thousands or millions of farmers document them across villages, districts, states, and countries, they can form an extraordinary picture of what is happening to agriculture and the environment.
            </p>
            <p>Farmers Pulse wants to make these observations useful.</p>

            <h2 id="local-knowledge">Local Knowledge Matters</h2>
            <p>A weather forecast may cover an entire district or county. But farming happens in individual fields.</p>
            <p>
              Rainfall can vary dramatically within a few kilometers. One farm may receive heavy rain while another nearby remains dry. Soil varies. Elevation varies. Drainage varies. Wind varies. Vegetation varies. Pests vary.
            </p>
            <p>That is why hyperlocal agricultural information can be extremely valuable.</p>
            <p>Farmers Pulse should allow farmers to share observations tied to their approximate location. For example:</p>
            <ul>
              <li>"Heavy rainfall this afternoon — standing water in rice fields."</li>
              <li>"Tomato leaf disease appearing in several plants."</li>
              <li>"Mango flowering started about two weeks earlier than last year."</li>
              <li>"Armyworms observed in maize."</li>
              <li>"Well water level unusually low."</li>
            </ul>
            <p>When similar reports appear from several farms in the same area, Farmers Pulse can begin identifying patterns.</p>

            <h2 id="weather">Weather and Agriculture</h2>
            <p>Weather remains one of the greatest influences on farming.</p>
            <p>Farmers Pulse can help organize weather information around agricultural needs rather than simply displaying temperature. Useful agricultural weather information can include:</p>
            <ul>
              <li>rainfall &amp; rainfall accumulation</li>
              <li>temperature &amp; humidity</li>
              <li>wind, thunderstorms, hail</li>
              <li>frost, snow, heat waves</li>
              <li>drought &amp; flooding</li>
              <li>soil moisture &amp; cloud cover</li>
              <li>extreme weather alerts</li>
            </ul>
            <p>The platform can eventually connect weather conditions directly to crops. Instead of telling a farmer only <em>"Heavy rain expected"</em>, Farmers Pulse could eventually provide context such as:</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              "Heavy rainfall may increase the risk of waterlogging in low-lying vegetable fields."
            </blockquote>
            <p>or:</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              "High humidity may increase fungal disease pressure in certain crops."
            </blockquote>
            <p>
              The objective is not to replace agricultural experts or official weather services. It is to help farmers understand what changing conditions may mean for their farms.
            </p>

            <h2 id="crop-intelligence">Crop Intelligence</h2>
            <p>Farmers Pulse can build a global crop knowledge library. Major crop categories can include:</p>
            <h3>Cereals</h3>
            <p>Rice · Wheat · Maize/Corn · Barley · Sorghum · Millets · Oats · Rye</p>
            <h3>Pulses and Legumes</h3>
            <p>Beans · Chickpeas · Lentils · Cowpea · Pigeon Pea · Peas · Groundnut/Peanut · Faba Bean</p>
            <h3>Roots and Tubers</h3>
            <p>Potato · Sweet Potato · Cassava/Yuca · Yam · Taro</p>
            <h3>Vegetables</h3>
            <p>Tomato · Onion · Cabbage · Cauliflower · Carrot · Eggplant · Peppers · Cucumber · Pumpkin · Leafy Greens</p>
            <h3>Fruits</h3>
            <p>Banana · Mango · Citrus · Apple · Grapes · Papaya · Pineapple · Coconut · Avocado · Berries</p>
            <h3>Commercial Crops</h3>
            <p>Sugarcane · Coffee · Tea · Cocoa · Cotton · Rubber · Oil Palm · Spices</p>
            <p>Each crop page can eventually include:</p>
            <ul>
              <li>crop overview &amp; varieties</li>
              <li>planting periods · temperature &amp; rainfall requirements</li>
              <li>soil preferences · irrigation · fertilizer information</li>
              <li>common pests &amp; diseases</li>
              <li>harvesting, storage &amp; market information</li>
              <li>farmer observations · current news · research links</li>
            </ul>
            <p>This turns Farmers Pulse into a continuously growing agricultural reference system.</p>

            <h2 id="crop-reports">Farmers Reporting Crop Conditions</h2>
            <p>One particularly useful feature could be a simple <strong>Crop Pulse</strong> report. A farmer selects a crop and reports its condition. For example:</p>
            <div className="rounded-2xl border border-black/10 bg-white/70 p-5 font-mono-tech text-sm tracking-wide text-[#241209] not-italic">
              <div>Crop: <strong>Rice</strong></div>
              <div>Growth Stage: <strong>Flowering</strong></div>
              <div>Condition: <strong>Good</strong></div>
              <div>Rainfall: <strong>Above normal</strong></div>
              <div>Pests: <strong>Minor</strong></div>
              <div>Disease: <strong>None observed</strong></div>
              <div>Notes: <em>Heavy rain during the last three days.</em></div>
              <div>Photos: <em>Attached</em></div>
            </div>
            <p>When thousands of such observations accumulate, Farmers Pulse could produce regional agricultural maps:</p>
            <ul>
              <li>Rice Pulse — Kerala</li>
              <li>Maize Pulse — Kenya</li>
              <li>Wheat Pulse — Punjab</li>
              <li>Coffee Pulse — Colombia</li>
            </ul>
            <p>This could eventually provide a valuable grassroots view of agricultural conditions around the world.</p>

            <h2 id="calendars">Planting and Harvest Calendars</h2>
            <p>Agricultural calendars remain extremely valuable. Farmers Pulse can create regional crop calendars showing:</p>
            <p className="font-mono-tech text-sm tracking-wider text-[#3A3830]">
              Land Preparation → Planting → Growing → Flowering → Fruit Development → Harvest → Post-Harvest
            </p>
            <p>
              Because agricultural timing varies by region, these calendars should eventually be localized. A tomato planting calendar for Oregon may be very different from one for Kerala. A rice calendar in India may differ considerably from a rice calendar in Vietnam.
            </p>
            <p>Farmers Pulse should therefore allow agricultural calendars to be organized geographically.</p>

            <h2 id="pests">Pests and Diseases</h2>
            <p>Crop pests and diseases can spread rapidly. Early observations can be extremely valuable. Farmers could report:</p>
            <ul>
              <li>pest type · crop affected · approximate location</li>
              <li>date observed · severity</li>
              <li>photos · treatment attempted · results</li>
            </ul>
            <p>Reports from multiple farms could help identify emerging local problems. Farmers Pulse could display:</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              <strong>Pest Pulse:</strong> Increasing reports of aphids in nearby vegetable farms.
              <br />
              <strong>Disease Pulse:</strong> Multiple reports of leaf spot disease affecting tomato plants.
            </blockquote>
            <p>
              Whenever possible, users should be directed to agricultural extension services, universities, government agencies, and qualified agricultural professionals for diagnosis and treatment recommendations.
            </p>

            <h2 id="soil-pulse">Soil Pulse</h2>
            <p>Farmers Pulse can also encourage farmers to understand and document their soils. Possible observations include:</p>
            <ul>
              <li>soil type · soil color · soil moisture</li>
              <li>drainage · erosion · compaction</li>
              <li>organic matter · pH · nutrient testing</li>
              <li>earthworm activity</li>
            </ul>
            <p>Farmers could maintain a historical record for each field. Over time, this creates a <strong>soil diary</strong>. A farmer could compare 2026 Soil Condition with 2028 Soil Condition and observe whether management practices are improving soil health.</p>

            <h2 id="water-pulse">Water Pulse</h2>
            <p>Water is increasingly one of agriculture's most important resources. Farmers Pulse can track:</p>
            <ul>
              <li>wells · ponds · reservoirs · streams</li>
              <li>irrigation canals · rainfall harvesting</li>
              <li>groundwater conditions · flooding · drought</li>
              <li>water restrictions</li>
            </ul>
            <p>Farmers could report changes such as:</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              "Farm pond is approximately half its normal level."
              <br />
              "Stream flow much lower than this time last year."
            </blockquote>
            <p>Over many years, these observations could create valuable local environmental records.</p>

            <h2 id="pollinators">Pollinators and Beneficial Insects</h2>
            <p>Not every insect is a pest. Farmers depend heavily on pollinators and other beneficial organisms. Farmers Pulse should encourage observations of:</p>
            <ul>
              <li>honeybees · native bees</li>
              <li>butterflies · moths · beetles</li>
              <li>dragonflies · ladybugs</li>
              <li>earthworms · spiders</li>
              <li>other beneficial organisms</li>
            </ul>
            <p>Changes in these populations may provide important clues about ecosystem health.</p>

            <h2 id="birds">Birds as Agricultural Indicators</h2>
            <p>Birds can also tell us a great deal about the health of farmland. Farmers can observe:</p>
            <ul>
              <li>number of bird species &amp; nesting activity</li>
              <li>migratory birds · insect-eating birds · water birds</li>
              <li>disappearance of familiar species</li>
            </ul>
            <p>A healthy agricultural landscape often supports more than crops. It supports an entire ecosystem.</p>

            <h2 id="livestock">Livestock Pulse</h2>
            <p>Farmers Pulse can eventually include information for livestock and poultry farmers. Categories could include:</p>
            <p>Cattle · Buffalo · Goats · Sheep · Pigs · Chickens · Ducks · Turkeys · Rabbits · Other farm animals</p>
            <p>Farmers could document feeding, breeding, pasture, heat stress, weather effects, animal health, disease alerts, water availability, and productivity.</p>

            <h2 id="extreme-weather">Extreme Weather and Disaster Reporting</h2>
            <p>Agriculture can be devastated by sudden events. Farmers Pulse can allow rapid reporting of:</p>
            <ul>
              <li>drought · flooding · landslides</li>
              <li>hail · frost · cyclones · hurricanes · tornadoes</li>
              <li>wildfires · heat waves · severe storms</li>
            </ul>
            <p>Farmers could upload photographs and document crop damage. Over time, these reports could help researchers, agricultural organizations, relief groups, and governments understand how extreme weather affects farming communities.</p>

            <h2 id="climate">Climate Change Through Farmers' Eyes</h2>
            <p>Climate change is often discussed using global statistics. Farmers experience it locally.</p>
            <blockquote className="border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 italic">
              "The rains used to begin in June. Now they often begin much later."
              <br />
              "Fruit trees are flowering earlier."
              <br />
              "Our well never went dry when I was younger."
            </blockquote>
            <p>These observations matter. Farmers Pulse can create a long-term archive of farmer observations. Future generations may be able to see how:</p>
            <ul>
              <li>planting seasons changed</li>
              <li>rainfall shifted</li>
              <li>pests moved</li>
              <li>flowering dates changed</li>
              <li>harvest times changed</li>
              <li>water availability changed</li>
              <li>crop varieties changed</li>
            </ul>
            <p>This could become one of the most valuable aspects of Farmers Pulse.</p>

            <h2 id="generations">Farming Knowledge Across Generations</h2>
            <p>Modern agriculture has tremendous scientific knowledge. But traditional farmers also possess valuable knowledge accumulated through generations.</p>
            <p>Farmers Pulse should provide a place to document that knowledge before it disappears. Farmers could share:</p>
            <ul>
              <li>traditional planting practices · seed preservation</li>
              <li>irrigation techniques · soil improvement methods</li>
              <li>natural pest control · crop rotation · intercropping</li>
              <li>traditional weather indicators · local crop varieties</li>
              <li>harvesting methods · food preservation</li>
            </ul>
            <p>This creates a bridge between traditional knowledge and modern science.</p>

            <h2 id="diaries">Farm Diaries</h2>
            <p>Every farmer should eventually be able to maintain a private or public <strong>Farm Diary</strong>. Entries might include:</p>
            <div className="rounded-2xl border border-black/10 bg-white/70 p-5 font-mono-tech text-sm tracking-wide text-[#241209]">
              <div>March 10 — Field prepared.</div>
              <div>March 15 — Seeds planted.</div>
              <div>March 22 — First emergence.</div>
              <div>April 4 — Heavy rain.</div>
              <div>April 18 — Fertilizer applied.</div>
              <div>May 2 — Pest observed.</div>
              <div>June 12 — Harvest started.</div>
            </div>
            <p>Farmers could attach photographs, videos, notes, expenses, weather observations, and crop measurements. Over several years, this becomes an extremely valuable farm record.</p>

            <h2 id="photo-docs">Photo Documentation</h2>
            <p>Smartphones have made agricultural documentation much easier. Farmers can photograph crops, pests, diseases, soil, irrigation, machinery, livestock, weeds, weather damage, and harvests.</p>
            <p>Farmers Pulse can organize these photographs automatically by <strong>farm, field, crop, date, location,</strong> and <strong>observation type</strong>. Eventually AI may help identify patterns or suggest possible species, pests, or diseases, while clearly distinguishing AI suggestions from expert diagnosis.</p>

            <h2 id="news-research">Agriculture News and Research</h2>
            <p>Farmers should also have easy access to reliable agricultural information. Farmers Pulse can gather and organize links from:</p>
            <ul>
              <li>agricultural universities · government agriculture departments</li>
              <li>extension services · research institutions</li>
              <li>international agricultural organizations · crop research centers</li>
              <li>farmer organizations · weather services</li>
            </ul>
            <p>Rather than trying to replace these sources, Farmers Pulse can act as a gateway connecting farmers to them.</p>

            <h2 id="global-local">A Global Platform With Local Value</h2>
            <p>Agriculture is global. But farming is local. Farmers Pulse can therefore be organized geographically:</p>
            <p className="font-mono-tech text-sm tracking-wider text-[#3A3830]">
              World → Country → State/Province → District/County → City/Village → Farm
            </p>
            <p>Users should be able to explore agricultural observations from their own region while also learning from farmers elsewhere.</p>
            <p>A farmer in India might learn how farmers in Africa manage drought. A farmer in Kenya might discover soil conservation methods used in Brazil. A farmer in California might study water management techniques from Israel or Australia.</p>
            <p>Agriculture has always involved sharing knowledge. Technology can now make that exchange global.</p>

            <h2 id="schools">Schools and Young Farmers</h2>
            <p>Farmers Pulse should also encourage students to participate. Schools could adopt:</p>
            <ul>
              <li>school gardens · weather stations · rainfall monitoring</li>
              <li>plant growth experiments · composting projects</li>
              <li>pollinator gardens · soil studies</li>
              <li>tree planting · crop diaries</li>
            </ul>
            <p>Students could contribute observations to NaturePulse and Farmers Pulse. This helps younger generations understand where food comes from and how agriculture depends on nature.</p>

            <h2 id="helping">Farmers Helping Farmers</h2>
            <p>Perhaps the most important element of Farmers Pulse is the farming community itself.</p>
            <p>Farmers know things that cannot always be learned from textbooks. One farmer may have discovered a better irrigation method. Another may know how to control a local pest. Another may have tested a drought-resistant variety. Another may know an effective composting technique.</p>
            <p>Farmers Pulse can create spaces where farmers share problems, observations, solutions, experiments, successes — and even failures, because unsuccessful experiments can teach valuable lessons too.</p>

            <h2 id="ai-assistant">AI as a Farming Assistant</h2>
            <p>Artificial intelligence can eventually help organize this enormous amount of agricultural information. Future Farmers Pulse tools could help farmers ask questions such as:</p>
            <ul>
              <li>"What crops are suitable for my area this month?"</li>
              <li>"Why are my tomato leaves turning yellow?"</li>
              <li>"What pests have farmers nearby reported?"</li>
              <li>"How much rainfall did my farm receive this season?"</li>
              <li>"When did mango flowering begin last year?"</li>
              <li>"What crops tolerate drought conditions?"</li>
            </ul>
            <p>AI should not replace farmers, agronomists, extension officers, veterinarians, scientists, or other agricultural professionals. Instead, it can help people find and understand information faster.</p>

            <h2 id="living-almanac">Building a Living Agricultural Almanac</h2>
            <p>The traditional farmer's almanac looked backward at accumulated knowledge to help farmers prepare for the future. Farmers Pulse can do something additional. It can look at:</p>
            <ul>
              <li>the past</li>
              <li>the present</li>
              <li>and eventually emerging patterns</li>
            </ul>
            <p>at the same time.</p>
            <p>Imagine millions of observations documenting rainfall, crops, pests, birds, pollinators, water, soil, harvests, weather, drought, flooding, planting, flowering, and food production.</p>
            <p>Together, they could create something much larger than an agricultural website. They could create a <strong>living record of agriculture on Earth</strong>.</p>

            <h2 id="join">Join the Farmers Pulse</h2>
            <p>Farmers Pulse is being developed as part of the broader NaturePulse mission to document and understand our changing natural world. Whether you are:</p>
            <ul>
              <li>a farmer · home gardener · agricultural student</li>
              <li>teacher · researcher · agronomist · extension worker</li>
              <li>farm organization · agricultural business · environmental observer</li>
            </ul>
            <p>your observations can contribute to this growing knowledge network.</p>
            <p>Look at your field. Look at the soil. Look at the clouds. Watch the insects. Listen to the birds. Observe the water. Document the crops. Record what is changing. Share what you learn.</p>
            <p>Because the future of agriculture may depend not only on what scientists measure from satellites and laboratories, but also on what millions of farmers observe every day.</p>
          </div>

          {/* Farm Diaries early-access opt-in */}
          <FarmDiariesOptIn />

          {/* Signature closer */}
          <div className="mt-12 rounded-2xl border border-black/10 bg-white/60 p-6 md:p-8">
            <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
              <Leaf size={16} weight="fill" />
              <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">Farmers Pulse</span>
            </div>
            <div className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight">
              Observe · Learn · Share · Grow
            </div>
            <p className="mt-3 text-[#3A3830]">
              A modern global agricultural almanac powered by farmers, nature, science, technology, and shared knowledge.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to={p("/report")}
                data-testid="farmers-almanac-cta-report"
                className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
              >
                Report what you see <ArrowRight size={10} weight="bold" />
              </Link>
              <Link
                to={p("/farms/new")}
                data-testid="farmers-almanac-cta-farm"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
              >
                Add my farm
              </Link>
              <Link
                to={home}
                data-testid="farmers-almanac-cta-home"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
              >
                Open Farmers Pulse
              </Link>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
}
